import axios, { AxiosError } from "axios";
import { plainToInstance } from "class-transformer";
import { DocumentRetrievalFailureReason, DocumentRetrievalTaskBatchDto, DocumentRetrievalTaskDownloadDto, PatientTEFCAAuthInfoDto, } from "healthex-common/document.retrieval.dtos";
import { err } from "healthex-common/result";
import { asEnum } from "healthex-common/utils";
import { authenticatedApiConfig, generateURL, retryTooManyRequests } from "./BaseApiService";
export class DocumentRetrievalService {
    constructor(authenticationProvider, environment) {
        this.authenticationProvider = authenticationProvider;
        this.environment = environment;
    }
    async getExistingRetrieval(patientId, input) {
        const response = await axios.post(generateURL(this.environment.API_BASE_URL, "/patients/:patientId/documents", { patientId }), { ...input, documentSelectionComplete: false }, authenticatedApiConfig(this.authenticationProvider));
        return response.data ? response.data : null;
    }
    async retrievePatientData(patientId, input) {
        return await retryTooManyRequests(async () => {
            var _a, _b, _c;
            try {
                const response = await axios.post(generateURL(this.environment.API_BASE_URL, "/patients/:patientId/documents/retrieve", {
                    patientId,
                }), input, authenticatedApiConfig(this.authenticationProvider));
                return response.data
                    ? plainToInstance(DocumentRetrievalTaskBatchDto, response.data)
                    : err(DocumentRetrievalFailureReason.GENERIC_ERROR);
            }
            catch (e) {
                if (e instanceof AxiosError && ((_a = e.response) === null || _a === void 0 ? void 0 : _a.status) === 403 && ((_b = e.response) === null || _b === void 0 ? void 0 : _b.data.error)) {
                    return err((_c = e.response) === null || _c === void 0 ? void 0 : _c.data.error);
                }
                throw e;
            }
        });
    }
    async getTaskBatch(patientId, batchId) {
        const response = await axios.post(generateURL(this.environment.API_BASE_URL, "/patients/:patientId/documents", { patientId }), { batchId }, authenticatedApiConfig(this.authenticationProvider));
        return response.data ? plainToInstance(DocumentRetrievalTaskBatchDto, response.data) : null;
    }
    async updateTaskBatch(patientId, batchId, input) {
        const response = await axios.put(`${this.environment.API_BASE_URL}/patients/${patientId}/documents/${batchId}`, input, authenticatedApiConfig(this.authenticationProvider));
        return response.data ? plainToInstance(DocumentRetrievalTaskBatchDto, response.data) : null;
    }
    async getTaskBatchDownloadLink(patientId, batchId) {
        const response = await axios.get(`${this.environment.API_BASE_URL}/patients/${patientId}/documents/${batchId}/download`, authenticatedApiConfig(this.authenticationProvider));
        return plainToInstance(DocumentRetrievalTaskDownloadDto, response.data);
    }
    async getMostRecentDownloadableOrProcessingTaskBatch(patientId) {
        const response = await axios.get(`${this.environment.API_BASE_URL}/patients/${patientId}/documents/most-recent-downloadable-or-processing`, authenticatedApiConfig(this.authenticationProvider));
        return plainToInstance(DocumentRetrievalTaskBatchDto, response.data);
    }
    async getMostRecentFinalizedTaskBatch(patientId, mustHaveRecords) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, "/patients/:patientId/documents/most-recent-finalized", {
            patientId,
        }), authenticatedApiConfig(this.authenticationProvider, { mustHaveRecords }));
        return response.data ? plainToInstance(DocumentRetrievalTaskBatchDto, response.data) : null;
    }
    async skipTEFCARetrieval(patientId, batchId) {
        try {
            const response = await axios.post(`${this.environment.API_BASE_URL}/patients/${patientId}/documents/${batchId}/tefca/skip`, {}, authenticatedApiConfig(this.authenticationProvider));
            return response.data
                ? plainToInstance(DocumentRetrievalTaskBatchDto, response.data)
                : err(DocumentRetrievalFailureReason.GENERIC_ERROR);
        }
        catch (e) {
            return this.checkThrownError(e);
        }
    }
    async startTEFCARetrieval(patientId, batchId) {
        try {
            const response = await axios.post(`${this.environment.API_BASE_URL}/patients/${patientId}/documents/${batchId}/tefca-retrieval`, {}, authenticatedApiConfig(this.authenticationProvider));
            return response.data
                ? plainToInstance(DocumentRetrievalTaskBatchDto, response.data)
                : err(DocumentRetrievalFailureReason.GENERIC_ERROR);
        }
        catch (e) {
            return this.checkThrownError(e);
        }
    }
    checkThrownError(e) {
        var _a;
        if (axios.isAxiosError(e)) {
            if ((_a = e.response) === null || _a === void 0 ? void 0 : _a.data) {
                const errorData = e.response.data;
                if (errorData.message) {
                    const failureReason = asEnum(DocumentRetrievalFailureReason)(errorData.message);
                    if (failureReason)
                        return err(failureReason);
                }
            }
        }
        throw e;
    }
    async getFacilitatedFHIRAuthUrl(patientId, batchId, fhirEndpoint, redirectUrl, authType) {
        var _a;
        const input = {
            fhirEndpoint,
            redirectUrl,
            authType,
        };
        const response = await axios.post(`${this.environment.API_BASE_URL}/patients/${patientId}/documents/${batchId}/facilitated-fhir/authorize-url`, input, authenticatedApiConfig(this.authenticationProvider));
        return (_a = response.data) !== null && _a !== void 0 ? _a : null;
    }
    async getFacilitatedFHIRRecords(patientId, batchId, locationId, fhirEndpoint, authType, redirectUri, code) {
        var _a;
        const input = {
            locationId,
            fhirEndpoint,
            redirectUri,
            authType,
            code,
        };
        const response = await axios.post(`${this.environment.API_BASE_URL}/patients/${patientId}/documents/${batchId}/facilitated-fhir/records`, input, authenticatedApiConfig(this.authenticationProvider));
        return (_a = response.data) !== null && _a !== void 0 ? _a : null;
    }
    async trackFastenRetrieval(patientId, batchId, connectionId, portalId, brandId, endpointId) {
        const response = await axios.post(`${this.environment.API_BASE_URL}/patients/${patientId}/documents/${batchId}/fasten`, { connectionId, portalId, brandId, endpointId }, authenticatedApiConfig(this.authenticationProvider));
        return response.data ? plainToInstance(DocumentRetrievalTaskBatchDto, response.data) : null;
    }
    async getTaskBatchDownloadPdfLink(patientId, batchId) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, "/patients/:patientId/documents/:batchId/download-pdf", {
            patientId,
            batchId,
        }), authenticatedApiConfig(this.authenticationProvider));
        return plainToInstance(DocumentRetrievalTaskDownloadDto, response.data);
    }
    async createDocumentDelivery(patientId, input) {
        await axios.post(generateURL(this.environment.API_BASE_URL, "/patients/:patientId/document-deliveries", { patientId }), input, authenticatedApiConfig(this.authenticationProvider));
    }
    async getPatientTEFCAAuthInfo(patientId) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, "/patients/:patientId/tefca-auth-info", { patientId }), authenticatedApiConfig(this.authenticationProvider));
        return plainToInstance(PatientTEFCAAuthInfoDto, response.data);
    }
}
